import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Container,
  GlobalStyles,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import getToken from '../auth/getToken';
import LlmAssistantPanel from './Llm/LlmAssistantPanel';
import PageHeader from './common/PageHeader';
import TranscriptionTipsCard from './Transcription/TranscriptionTipsCard';
import SpeakerToolbar from './Transcription/SpeakerToolbar';
import TranscriptionSegment from './Transcription/TranscriptionSegment';
import SpeakerAssistantDialog from './Transcription/SpeakerAssistantDialog';
import DownloadDialog from './Transcription/DownloadDialog';
import AudioPlayerBar from './Transcription/AudioPlayerBar';
import { buildExport } from '../helper/transcriptExport';
import {
  dismissSpeakerAssistant,
  fetchTranscriptionResult,
  splitSegmentAtWord,
  updateSegmentText,
} from '../helper/transcriptionResultApi';
import {
  filterSegmentsBySpeaker,
  groupConsecutiveBySpeaker,
  rebuildTranscription,
  speakerForSegment,
} from '../helper/transcriptionResultUtils';
import { useUserPreferences } from '../hooks/useUserPreferences';
import TranscriptionSpeakerBlock from './Transcription/TranscriptionSpeakerBlock';
import WordActionPopover from './Transcription/WordActionPopover';
import SpeakerSplitDialog from './Transcription/SpeakerSplitDialog';

const SPEAKER_COLORS = [
  '#1976d2',
  '#d32f2f',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#00796b',
  '#5d4037',
  '#455a64',
  '#e91e63',
  '#ff9800',
];

const DEFAULT_SPEAKER_RE = /^SPEAKER_\d+$/;

const TranscriptionResultView = () => {
  const { jobId } = useParams();
  const { instance } = useMsal();

  const [loading, setLoading] = useState(true);
  const [audioUrl, setAudioUrl] = useState('');
  const [originalFileName, setOriginalFileName] = useState('');
  const [transcriptFilename, setTranscriptFilename] = useState('');
  const [transcription, setTranscription] = useState('');
  const [segments, setSegments] = useState([]);
  const [wasSpeakerDetection, setWasSpeakerDetection] = useState(false);
  const [activeWord, setActiveWord] = useState(null);
  const [activeSpeaker, setActiveSpeaker] = useState(null);

  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantDismissed, setAssistantDismissed] = useState(false);
  const [speakerAssistantDismissed, setSpeakerAssistantDismissed] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);

  // View-Mode (standard | compact) persists across devices via MongoDB.
  const { ui: userUi, setUiKey } = useUserPreferences();
  const viewMode = userUi.transcription_view_mode || 'standard';
  const handleViewModeChange = useCallback(
    (mode) => setUiKey('transcription_view_mode', mode),
    [setUiKey],
  );

  // Word-action popover (opens on word click; offers audio jump + speaker split).
  const [wordPopover, setWordPopover] = useState(null);
  // shape: { anchorEl, segmentIndex, wordIndex, totalWords, wordStart }
  const [splitDialog, setSplitDialog] = useState(null);
  // shape: { segmentIndex, wordIndex }

  const audioRef = useRef(null);
  const assistantRef = useRef(null);
  const autoAssistantOpenRef = useRef(false);

  const scrollToAssistant = () => {
    assistantRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const uniqueSpeakers = React.useMemo(
    () => [...new Set(segments.map((s, idx) => speakerForSegment(s, idx)))],
    [segments],
  );

  const filteredSegments = useMemo(
    () => filterSegmentsBySpeaker(segments, activeSpeaker),
    [activeSpeaker, segments],
  );

  useEffect(() => {
    if (activeSpeaker && !uniqueSpeakers.includes(activeSpeaker)) {
      setActiveSpeaker(null);
    }
  }, [activeSpeaker, uniqueSpeakers]);

  const displayedActiveWord = useMemo(
    () =>
      activeWord &&
      filteredSegments.some((item) => item.originalIndex === activeWord.segmentIndex)
        ? activeWord
        : null,
    [activeWord, filteredSegments],
  );

  const updateSegmentLocal = useCallback(
    (segmentIndex, updatedSegment) => {
      setSegments((prev) => {
        const next = [...prev];
        next[segmentIndex] = updatedSegment;
        setTranscription(rebuildTranscription(next));
        return next;
      });
    },
    [],
  );

  const handleSpeakerClick = useCallback((speaker) => {
    setActiveSpeaker((current) => (current === speaker ? null : speaker));
  }, []);

  const markSpeakerAssistantDismissed = useCallback(async () => {
    setSpeakerAssistantDismissed(true);
    try {
      await dismissSpeakerAssistant(instance, jobId);
    } catch (error) {
      console.error('[Result] Speaker assistant dismissal failed:', error);
    }
  }, [instance, jobId]);

  const saveSegmentText = useCallback(
    async (segmentIndex, text) => {
      const data = await updateSegmentText(instance, jobId, segmentIndex, text);
      if (Array.isArray(data.segments)) setSegments(data.segments);
      if (typeof data.transcription === 'string') {
        setTranscription(data.transcription);
      } else if (data.segment) {
        updateSegmentLocal(segmentIndex, data.segment);
      }
      return data.segment;
    },
    [instance, jobId, updateSegmentLocal],
  );

  const handleAssistantClose = useCallback(() => {
    const wasAutoOpen = autoAssistantOpenRef.current;
    autoAssistantOpenRef.current = false;
    setAssistantOpen(false);
    setAssistantDismissed(true);
    if (wasAutoOpen) {
      markSpeakerAssistantDismissed();
    }
  }, [markSpeakerAssistantDismissed]);

  const handleAssistantSaved = useCallback(
    (updated) => {
      setSegments(updated);
      setTranscription(rebuildTranscription(updated));
    },
    [],
  );

  const getSpeakerColor = (speakerId) => {
    if (!speakerId) return '#666';
    const idx = uniqueSpeakers.indexOf(speakerId);
    if (idx < 0) return '#666';
    return SPEAKER_COLORS[idx % SPEAKER_COLORS.length];
  };

  const handleWordClick = useCallback((start) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = start;
    audioRef.current.play();
  }, []);

  // Word click opens the action popover instead of jumping audio directly.
  // The popover lets the user choose between audio-jump and starting a new
  // speaker at that word.
  const handleWordAction = useCallback((payload) => {
    setWordPopover(payload);
  }, []);

  const handlePopoverClose = useCallback(() => setWordPopover(null), []);

  const handlePopoverJumpAudio = useCallback(() => {
    if (!wordPopover) return;
    if (typeof wordPopover.wordStart === 'number') {
      handleWordClick(wordPopover.wordStart);
    }
  }, [wordPopover, handleWordClick]);

  const handlePopoverSplit = useCallback(() => {
    if (!wordPopover) return;
    setSplitDialog({
      segmentIndex: wordPopover.segmentIndex,
      wordIndex: wordPopover.wordIndex,
    });
  }, [wordPopover]);

  const handleSplitDialogClose = useCallback(() => setSplitDialog(null), []);

  const handleSplitConfirm = useCallback(
    async (newSpeaker) => {
      if (!splitDialog) return;
      try {
        const data = await splitSegmentAtWord(
          instance,
          jobId,
          splitDialog.segmentIndex,
          splitDialog.wordIndex,
          newSpeaker,
        );
        if (Array.isArray(data.segments)) setSegments(data.segments);
        if (typeof data.transcription === 'string') {
          setTranscription(data.transcription);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Result] segment split failed', e);
      } finally {
        setSplitDialog(null);
        setWordPopover(null);
      }
    },
    [instance, jobId, splitDialog],
  );

  const splitDisabled = wordPopover
    ? wordPopover.wordIndex <= 0
      || wordPopover.wordIndex >= (wordPopover.totalWords || 0)
    : true;
  const splitDisabledReason = splitDisabled
    ? 'Am ersten oder letzten Wort des Segments nicht möglich. Bitte ein Wort weiter innen anklicken.'
    : '';

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const data = await fetchTranscriptionResult(instance, jobId);
        const fileName = data.file_url ? data.file_url.split('/').pop() : '';
        setOriginalFileName(fileName);
        if (data.transcript_filename) {
          setTranscriptFilename(data.transcript_filename);
        } else if (fileName) {
          const baseName = fileName.replace(/\.[^/.]+$/, '');
          setTranscriptFilename(`${baseName}.txt`);
        }
        setTranscription(data.transcription);
        setSegments(data.segments || []);
        setWasSpeakerDetection(data.segments && data.segments.length > 0);
        setSpeakerAssistantDismissed(Boolean(data.speaker_assistant_dismissed));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [jobId, instance]);

  useEffect(() => {
    if (
      loading ||
      assistantDismissed ||
      speakerAssistantDismissed ||
      !segments.length
    ) return;
    const hasDefault = segments.some((s) =>
      DEFAULT_SPEAKER_RE.test(s.speaker || ''),
    );
    if (hasDefault) {
      autoAssistantOpenRef.current = true;
      setAssistantOpen(true);
    }
  }, [loading, segments, assistantDismissed, speakerAssistantDismissed]);

  useEffect(() => {
    const fetchAudio = async () => {
      if (!originalFileName) return;
      try {
        const token = await getToken(instance, [
          `${process.env.REACT_APP_CLIENT_ID}/.default`,
        ]);
        const url =
          `${process.env.REACT_APP_BACKEND_URL}/audios/` +
          encodeURIComponent(originalFileName);
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Error fetching audio file');
        const blob = await res.blob();
        setAudioUrl(URL.createObjectURL(blob));
      } catch (e) {
        console.error('[Result] Audio fetch error:', e);
      }
    };
    fetchAudio();
  }, [originalFileName, instance]);

  const flatWords = useMemo(() => {
    const out = [];
    segments.forEach((seg, sIdx) => {
      (seg.words || []).forEach((w, wIdx) => {
        if (typeof w.start === 'number' && typeof w.end === 'number') {
          out.push({
            start: w.start,
            end: w.end,
            segmentIndex: sIdx,
            wordIndex: wIdx,
          });
        }
      });
    });
    return out;
  }, [segments]);

  const pointerRef = useRef(0);
  const activeWordRef = useRef(null);

  useEffect(() => {
    pointerRef.current = 0;
  }, [flatWords]);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const commit = (next) => {
      const prev = activeWordRef.current;
      const same =
        (prev === null && next === null) ||
        (prev !== null &&
          next !== null &&
          prev.segmentIndex === next.segmentIndex &&
          prev.wordIndex === next.wordIndex);
      if (same) return;
      activeWordRef.current = next;
      setActiveWord(next);
    };

    const handleTimeUpdate = () => {
      const t = audioElement.currentTime;
      const n = flatWords.length;
      if (n === 0) {
        commit(null);
        return;
      }

      let idx = pointerRef.current;
      if (idx >= n) idx = 0;

      // Schneller Pfad: Pointer zeigt bereits auf das aktuelle Wort
      const cur = flatWords[idx];
      if (cur && t >= cur.start && t <= cur.end) return;

      // Sequentielle Vorwaerts-Suche (haeufigster Fall beim normalen Abspielen)
      let found = -1;
      if (cur && t > cur.end) {
        const limit = Math.min(n, idx + 20);
        for (let i = idx + 1; i < limit; i++) {
          const w = flatWords[i];
          if (t < w.start) break;
          if (t <= w.end) {
            found = i;
            break;
          }
        }
      }

      // Fallback: binaere Suche (Seek, Sprung, Pause)
      if (found === -1) {
        let lo = 0;
        let hi = n - 1;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          const w = flatWords[mid];
          if (t < w.start) hi = mid - 1;
          else if (t > w.end) lo = mid + 1;
          else {
            found = mid;
            break;
          }
        }
      }

      if (found === -1) {
        commit(null);
        return;
      }

      pointerRef.current = found;
      const w = flatWords[found];
      commit({ segmentIndex: w.segmentIndex, wordIndex: w.wordIndex });
    };

    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    return () =>
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
  }, [flatWords]);

  const openDownloadDialog = () => setDownloadDialogOpen(true);

  const performDownload = ({ format, withSpeaker, groupBySpeaker }) => {
    const hasSegments = segments && segments.length > 0;
    const { content, extension, mime } = buildExport({
      format,
      segments,
      transcription,
      hasSegments,
      withSpeaker,
      groupBySpeaker,
    });

    const fallbackBase = `${jobId}-transkription`;
    const base = (transcriptFilename || `${fallbackBase}.txt`).replace(
      /\.[^/.]+$/,
      '',
    );
    const filename = `${base || fallbackBase}.${extension}`;

    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.href = url;
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);

    setDownloadDialogOpen(false);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Lade Transkriptionsergebnisse …
        </Typography>
      </Container>
    );
  }

  const hasSegments = segments && segments.length > 0;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <GlobalStyles
        styles={{
          '.tx-word': {
            cursor: 'pointer',
            borderRadius: 2,
            transition: 'background-color 80ms ease',
          },
          '.tx-word:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
          },
          '.tx-word-active': {
            backgroundColor: '#ffe0a3',
            padding: '0 3px',
            margin: '0 -3px',
          },
          '.tx-word-active:hover': {
            backgroundColor: '#ffe0a3',
          },
        }}
      />
      <PageHeader
        overline="Ergebnis"
        title="Transkription"
        subtitle={originalFileName || undefined}
      />

      <Box sx={{ mb: 3 }}>
        <AudioPlayerBar audioRef={audioRef} audioUrl={audioUrl} />
      </Box>

      {hasSegments ? (
        <>
          <SpeakerToolbar
            speakers={uniqueSpeakers}
            getSpeakerColor={getSpeakerColor}
            activeSpeaker={activeSpeaker}
            onSpeakerClick={handleSpeakerClick}
            onOpenAssistant={() => {
              autoAssistantOpenRef.current = false;
              setAssistantOpen(true);
            }}
            onOpenAiAssistant={scrollToAssistant}
            onDownload={openDownloadDialog}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
          />

          <Box sx={{ mb: 2 }}>
            <TranscriptionTipsCard />
          </Box>

          <Stack spacing={1.25}>
            {viewMode === 'compact'
              ? groupConsecutiveBySpeaker(filteredSegments).map((block) => (
                  <TranscriptionSpeakerBlock
                    key={`block-${block.items[0].originalIndex}`}
                    block={block}
                    speakerColor={getSpeakerColor(block.speaker)}
                    activeWord={displayedActiveWord}
                    onWordAction={handleWordAction}
                  />
                ))
              : filteredSegments.map(({ segment: seg, originalIndex }) => (
                  <TranscriptionSegment
                    key={originalIndex}
                    segment={seg}
                    index={originalIndex}
                    speakerColor={getSpeakerColor(speakerForSegment(seg, originalIndex))}
                    activeWordIndex={
                      displayedActiveWord &&
                      displayedActiveWord.segmentIndex === originalIndex
                        ? displayedActiveWord.wordIndex
                        : -1
                    }
                    onWordAction={handleWordAction}
                    onWordClick={handleWordClick}
                    onSaveText={saveSegmentText}
                  />
                ))}
          </Stack>

          <WordActionPopover
            open={Boolean(wordPopover)}
            anchorEl={wordPopover?.anchorEl || null}
            onClose={handlePopoverClose}
            onJumpAudio={handlePopoverJumpAudio}
            onSplitHere={handlePopoverSplit}
            splitDisabled={splitDisabled}
            splitDisabledReason={splitDisabledReason}
          />

          <SpeakerSplitDialog
            open={Boolean(splitDialog)}
            onClose={handleSplitDialogClose}
            existingSpeakers={uniqueSpeakers}
            getSpeakerColor={getSpeakerColor}
            onConfirm={handleSplitConfirm}
          />
        </>
      ) : (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.7,
            }}
          >
            {transcription}
          </Typography>
        </Paper>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={openDownloadDialog}
        >
          Transkription herunterladen
        </Button>
      </Box>

      <DownloadDialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        onConfirm={performDownload}
        hasSpeakers={wasSpeakerDetection}
        defaultGrouped={viewMode === 'compact'}
      />

      <SpeakerAssistantDialog
        open={assistantOpen}
        segments={segments}
        jobId={jobId}
        onClose={handleAssistantClose}
        onSaved={handleAssistantSaved}
      />

      {!loading && jobId && (
        <Box ref={assistantRef} sx={{ scrollMarginTop: 16 }}>
          <LlmAssistantPanel jobId={jobId} />
        </Box>
      )}
    </Container>
  );
};

export default TranscriptionResultView;
